import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ComisionesTrabajador.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';

const ComisionesTrabajador = ({ userData }) => {
    const [comisiones, setComisiones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [busqueda, setBusqueda] = useState('');
    const [fechaInicio, setFechaInicio] = useState(null);
    const [fechaFin, setFechaFin] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuthentication = () => {
            const isAuth = localStorage.getItem('isAuthenticated') === 'true';
            const role = localStorage.getItem('userRole');
            
            if (!isAuth || (role !== 'trabajador' && role !== 'admin')) {
                navigate('/Login');
                return false;
            }
            return true;
        };

        const loadUserData = () => {
            try {
                // Prioridad a props, luego a localStorage
                const data = userData || JSON.parse(localStorage.getItem('user'));
                
                if (!data || !data.id) {
                    navigate('/Login');
                    return;
                }
                
                setUserInfo(data);
                return data;
            } catch (error) {
                console.error('Error loading user data:', error);
                navigate('/Login');
                return null;
            }
        };

        if (checkAuthentication()) {
            const user = loadUserData();
            if (user) {
                fetchData(user.id);
            }
        }
    }, [navigate, userData]);

    const fetchData = async (userId) => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await axios.get(`https://backend-vbfh.onrender.com/api/comisiones/trabajador/${userId}`);
            
            if (response.data.success) {
                setComisiones(response.data.comisiones || []);
            } else {
                throw new Error(response.data.message || 'Error al cargar comisiones');
            }
        } catch (error) {
            console.error('Error fetching commissions:', error);
            setError(error.response?.data?.message || error.message || 'Error al cargar comisiones');
        } finally {
            setLoading(false);
        }
    };

    const aplicarFiltros = () => {
        let filtered = [...comisiones];
        
        if (busqueda) {
            filtered = filtered.filter(c => 
                (c.cliente_pedido?.toLowerCase().includes(busqueda.toLowerCase())) ||
                (c.numeroremision?.toString().includes(busqueda))
            );
        }
        
        if (filtroEstado !== 'todos') {
            filtered = filtered.filter(c => c.estado === filtroEstado);
        }
        
        if (fechaInicio && fechaFin) {
            const inicio = new Date(fechaInicio);
            inicio.setHours(0, 0, 0, 0);
            
            const fin = new Date(fechaFin);
            fin.setHours(23, 59, 59, 999);
            
            filtered = filtered.filter(c => {
                const fechaComision = new Date(c.fecha);
                return fechaComision >= inicio && fechaComision <= fin;
            });
        }
        
        return filtered;
    };

    const comisionesFiltradas = aplicarFiltros();

    const calcularTotalComision = (monto, porcentaje) => {
        return (monto * (porcentaje / 100)).toFixed(2);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const formatFecha = (fecha) => {
        if (!fecha) return 'N/A';
        try {
            const fechaObj = new Date(fecha);
            return fechaObj.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return 'Fecha inválida';
        }
    };

    const limpiarFiltros = () => {
        setBusqueda('');
        setFiltroEstado('todos');
        setFechaInicio(null);
        setFechaFin(null);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Cargando comisiones...</p>
                <button onClick={() => fetchData(userInfo?.id)} className="btn-reintentar">
                    Reintentar
                </button>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <p className="error-message">Error: {error}</p>
                <button onClick={() => fetchData(userInfo?.id)} className="btn-reintentar">
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="comisiones-trabajador-container">
            <div className="comisiones-header">
                <h1>Mis Comisiones</h1>
                
                {userInfo && (
                    <div className="user-info-card">
                        <h3>Información del Trabajador</h3>
                        <p><strong>Nombre:</strong> {userInfo.nombre}</p>
                        <p><strong>Porcentaje de comisión:</strong> {parseFloat(userInfo.porcentaje_comision || 0).toFixed(2)}%</p>
                    </div>
                )}
            </div>

            <div className="filtros-comisiones">
                <div className="filtro-group">
                    <label>Buscar:</label>
                    <input
                        type="text"
                        placeholder="Cliente o número de remisión..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="search-input"
                    />
                </div>
                
                <div className="filtro-group">
                    <label>Estado comisión:</label>
                    <select 
                        value={filtroEstado} 
                        onChange={(e) => setFiltroEstado(e.target.value)}
                        className="estado-select"
                    >
                        <option value="todos">Todos</option>
                        <option value="pendiente">Pendientes</option>
                        <option value="pagado">Pagadas</option>
                        <option value="rechazado">Rechazadas</option>
                    </select>
                </div>
                
                <div className="filtro-group rango-fechas">
                    <div className="date-picker-group">
                        <label>Desde:</label>
                        <DatePicker
                            selected={fechaInicio}
                            onChange={date => setFechaInicio(date)}
                            selectsStart
                            startDate={fechaInicio}
                            endDate={fechaFin}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Fecha inicio"
                            className="date-input"
                        />
                    </div>
                    
                    <div className="date-picker-group">
                        <label>Hasta:</label>
                        <DatePicker
                            selected={fechaFin}
                            onChange={date => setFechaFin(date)}
                            selectsEnd
                            startDate={fechaInicio}
                            endDate={fechaFin}
                            minDate={fechaInicio}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Fecha fin"
                            className="date-input"
                        />
                    </div>
                </div>
                
                <div className="filtro-buttons">
                    <button onClick={limpiarFiltros} className="btn-limpiar">
                        Limpiar filtros
                    </button>
                    
                    <button onClick={() => fetchData(userInfo?.id)} className="btn-refresh">
                        Actualizar datos
                    </button>
                </div>
            </div>

            <div className="resumen-comisiones">
                <div className="resumen-card pendiente">
                    <h3>Total Pendiente</h3>
                    <p>{formatPrice(
                        comisionesFiltradas
                            .filter(c => c.estado === 'pendiente')
                            .reduce((sum, c) => sum + parseFloat(c.valor_comision || calcularTotalComision(c.monto, c.porcentaje)), 0)
                    )}</p>
                </div>
                
                <div className="resumen-card pagado">
                    <h3>Total Pagado</h3>
                    <p>{formatPrice(
                        comisionesFiltradas
                            .filter(c => c.estado === 'pagado')
                            .reduce((sum, c) => sum + parseFloat(c.valor_comision || calcularTotalComision(c.monto, c.porcentaje)), 0)
                    )}</p>
                </div>
                
                <div className="resumen-card total">
                    <h3>Total Comisiones</h3>
                    <p>{formatPrice(
                        comisionesFiltradas
                            .reduce((sum, c) => sum + parseFloat(c.valor_comision || calcularTotalComision(c.monto, c.porcentaje)), 0)
                    )}</p>
                </div>
            </div>

            <div className="comisiones-list-container">
                {comisionesFiltradas.length === 0 ? (
                    <div className="no-comisiones">
                        <p>No hay comisiones registradas con los filtros actuales</p>
                        <button onClick={() => limpiarFiltros()} className="btn-limpiar">
                            Limpiar filtros
                        </button>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="comisiones-table">
                            <thead>
                                <tr>
                                    <th>Remisión</th>
                                    <th>Cliente</th>
                                    <th>Monto Pedido</th>
                                    <th>% Comisión</th>
                                    <th>Total Comisión</th>
                                    <th>Estado Pedido</th>
                                    <th>Pago Cliente</th>
                                    <th>Fecha</th>
                                    <th>Estado Comisión</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comisionesFiltradas.map(comision => (
                                    <tr key={comision.id || comision._id}>
                                        <td>{comision.numeroremision || 'N/A'}</td>
                                        <td>{comision.cliente_pedido || 'N/A'}</td>
                                        <td>{formatPrice(comision.monto || 0)}</td>
                                        <td>{parseFloat(comision.porcentaje || 0).toFixed(2)}%</td>
                                        <td>{formatPrice(comision.valor_comision || calcularTotalComision(comision.monto, comision.porcentaje))}</td>
                                        <td className={`estado-pedido ${(comision.estado_pedido?.toLowerCase() || '').replace(' ', '-')}`}>
                                            {comision.estado_pedido || 'N/A'}
                                        </td>
                                        <td className={`pago-cliente ${comision.cliente_pago === 'Sí' ? 'pagado' : 'no-pagado'}`}>
                                            {comision.cliente_pago === 'Sí' ? 'Pagado' : 'No pagado'}
                                        </td>
                                        <td>{formatFecha(comision.fecha)}</td>
                                        <td className={`estado-comision ${comision.estado || 'pendiente'}`}>
                                            {comision.estado || 'Pendiente'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComisionesTrabajador;